// contexts/CurrencyContext.js
import useSettings from "@/hooks/useSettings";
import { createContext, useContext, useEffect, useState } from "react";

const currencies = [
  {
    name: "Rwanda Francs (FRW)",
    code: "FRW",
    symbol: "FRWFRW",
    position: "prefix",
    status: "active",
    locale: "en-US",
  },
  {
    name: "United States Dollar (USD)",
    code: "USD",
    symbol: "$",
    position: "prefix",
    status: "active",
    locale: "en-US",
  },
  {
    name: "Congolese Franc (CDF)",
    code: "CDF",
    symbol: "FC",
    position: "prefix",
    status: "active",
    locale: "fr-CF",
  },
];

// @ts-ignore
const CurrencyContext = createContext<any>();

export const CurrencyProvider = ({ children }) => {
  const { settings = {} } = useSettings();

  const [currentCurrency, setCurrentCurrency] = useState(settings.currency);

  useEffect(() => {
    setCurrentCurrency(settings.currency);
  }, [settings.currency]);

  const switchCurrency = (currencyCode: string) => {
    if (currencies.find((currency) => currency.code === currencyCode)) {
      setCurrentCurrency(currencyCode);
    }
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    const currency = currencies.find(
      (currency) => currency?.code === currentCurrency,
    );

    // Amounts arrive as numbers, as Postgres numeric strings, or missing
    // entirely (an endpoint that never computed the field). None of those may
    // ever render as "FRWNaN" on an invoice - an unknown amount is zero here.
    const value = Number(amount);
    const numeric = Number.isFinite(value) ? value : 0;

    try {
      const hasDecimals = numeric % 1 !== 0;
      const formatter = new Intl.NumberFormat(currency.locale, {
        style: "currency",
        currencyDisplay: "narrowSymbol",
        currency: currency.code,
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0,
      });

      let formatted = formatter.format(numeric);

      return formatted;
    } catch (error) {
      console.error("Currency formatting error:", error);
      return `${numeric.toLocaleString()}`;
    }
  };

  const value = {
    currentCurrency,
    currency: currencies.find((currency) => currency?.code === currentCurrency),
    switchCurrency,
    fmtCur: formatCurrency,
    currencies,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
};
